import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import * as RetornoAPI from '../../../api/arquivoRetorno';
import { dataRetorno, moedaRetorno, rotuloRetorno } from './retornoFormatadores';

const mensagem = e => e?.response?.data?.message || 'Não foi possível consultar o servidor. Tente atualizar a consulta.';
const dinheiro = [['valorTitulo', 'Principal'], ['valorPago', 'Pago'], ['acrescimos', 'Acréscimos'], ['desconto', 'Desconto'], ['abatimento', 'Abatimento'], ['iof', 'IOF'], ['valorLiquido', 'Líquido informado'], ['outrasDespesas', 'Outras despesas'], ['outrosCreditos', 'Outros créditos'], ['tarifa', 'Tarifa']];
const paginaInicial = () => ({ page: 0, size: 10 });
function Paginacao({ total, valor, alterar, disabled }) {
    return <TablePagination component="div" count={total} page={valor.page} rowsPerPage={valor.size}
        rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Por página"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        backIconButtonProps={{ disabled: disabled || valor.page === 0, title: 'Página anterior' }}
        nextIconButtonProps={{ disabled: disabled || (valor.page + 1) * valor.size >= total, title: 'Próxima página' }}
        SelectProps={{ disabled }} onPageChange={(_, page) => alterar({ ...valor, page })}
        onRowsPerPageChange={e => alterar({ page: 0, size: Number(e.target.value) })} />;
}

export default function RetornosDialog({ conta, convenio, onClose }) {
    const [lista, setLista] = useState([]);
    const [total, setTotal] = useState(0);
    const [pagina, setPagina] = useState(paginaInicial);
    const [paginaEventos, setPaginaEventos] = useState(paginaInicial);
    const [selecionado, setSelecionado] = useState(null);
    const [detalhe, setDetalhe] = useState(null);
    const [arquivo, setArquivo] = useState(null);
    const [permissoes, setPermissoes] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [lendo, setLendo] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState('');
    const [erroDetalhe, setErroDetalhe] = useState('');
    const [erroEnvio, setErroEnvio] = useState('');
    const [sucesso, setSucesso] = useState('');
    const [atualizacao, setAtualizacao] = useState(0);
    const vivo = useRef(true);
    const trava = useRef(false);
    const input = useRef(null);
    const contaUuid = conta.uuid, convenioUuid = convenio.uuid;
    useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);

    useEffect(() => {
        let atual = true;
        setCarregando(true); setErro(''); setLista([]); setTotal(0); setPermissoes(null);
        RetornoAPI.listarRetornos(contaUuid, convenioUuid, { start: pagina.page * pagina.size, size: pagina.size })
            .then(({ data }) => { if (atual) { setLista(data.data || []); setTotal(data.meta?.totalRowCount || 0); setPermissoes(data.permissoes || null); } })
            .catch(e => { if (atual) setErro(mensagem(e)); })
            .finally(() => { if (atual) setCarregando(false); });
        return () => { atual = false; };
    }, [contaUuid, convenioUuid, pagina, atualizacao]);

    useEffect(() => {
        let atual = true;
        setDetalhe(null); setErroDetalhe('');
        if (!selecionado) { setLendo(false); return undefined; }
        setLendo(true);
        RetornoAPI.detalharRetorno(contaUuid, convenioUuid, selecionado, { start: paginaEventos.page * paginaEventos.size, size: paginaEventos.size })
            .then(({ data }) => { if (atual) setDetalhe(data); })
            .catch(e => { if (atual) setErroDetalhe(mensagem(e)); })
            .finally(() => { if (atual) setLendo(false); });
        return () => { atual = false; };
    }, [contaUuid, convenioUuid, selecionado, paginaEventos, atualizacao]);

    const podeImportar = permissoes?.incluir === true && !carregando;
    const abrir = uuid => { setPaginaEventos(paginaInicial()); setSelecionado(uuid); };
    const escolher = e => {
        const novo = e.target.files?.[0]; setErroEnvio(''); setSucesso(''); setArquivo(null);
        if (!novo) return;
        if (!novo.size || novo.size > 10 * 1024 * 1024) {
            setErroEnvio('Escolha um arquivo não vazio de até 10 MiB.'); e.target.value = ''; return;
        }
        setArquivo(novo);
    };
    const importar = async () => {
        if (trava.current || !podeImportar || !arquivo) return;
        trava.current = true; setEnviando(true); setErroEnvio(''); setSucesso('');
        try {
            const { data } = await RetornoAPI.importarRetorno(contaUuid, convenioUuid, arquivo);
            if (!vivo.current) return;
            setSucesso(data.message || 'Histórico importado. Nenhuma baixa ou despesa foi gerada.');
            setArquivo(null); if (input.current) input.current.value = '';
            setPagina(paginaInicial()); abrir(data.arquivo.uuid); setAtualizacao(v => v + 1);
        } catch (e) {
            if (!vivo.current) return;
            setErroEnvio(e?.response ? mensagem(e) : 'Não recebemos a confirmação do servidor. Consulte Atualizar histórico antes de tentar novamente; o arquivo pode ter sido recebido. A reimportação do mesmo arquivo é reconhecida.');
            setAtualizacao(v => v + 1);
        } finally {
            trava.current = false; if (vivo.current) setEnviando(false);
        }
    };
    return <Dialog open fullWidth maxWidth="lg" onClose={() => { if (!trava.current) onClose(); }}>
        <DialogTitle>Retornos bancários<Typography component="div" variant="body2" sx={{ mt: 1 }}>{conta.descricao} — {convenio.descricao}</Typography></DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
            <Alert severity="info" sx={{ mb: 2 }}>Importe o retorno Sicredi CNAB 240 para registrar e consultar as ocorrências. Esta etapa não efetua baixas nem gera despesas de tarifa.</Alert>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Importar arquivo</Typography>
                {!carregando && permissoes && !permissoes.incluir && <Alert severity="info" sx={{ mb: 2 }}>Importação indisponível: requer conta e convênio ativos e permissão de inclusão no submenu de retornos.</Alert>}
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                    <Button component="label" variant="outlined" disabled={!podeImportar || enviando}>Selecionar arquivo
                        <input ref={input} type="file" hidden onChange={escolher} disabled={!podeImportar || enviando} />
                    </Button>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{arquivo ? arquivo.name : 'Nenhum arquivo selecionado'}</Typography>
                    <Button type="button" variant="contained" disabled={!podeImportar || !arquivo || enviando} onClick={importar}>{enviando ? 'Importando…' : 'Importar para histórico'}</Button>
                </Stack>
                <Typography variant="caption" component="p" sx={{ mt: 1 }}>Um arquivo por vez, até 10 MiB. O conteúdo será validado pelo servidor.</Typography>
                {erroEnvio && <Alert severity="error" sx={{ mt: 2 }}>{erroEnvio}</Alert>}
                {sucesso && <Alert severity="success" sx={{ mt: 2 }}>{sucesso}</Alert>}
            </Paper>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Arquivos importados</Typography>
                <Button type="button" disabled={carregando || enviando} onClick={() => setAtualizacao(v => v + 1)}>Atualizar histórico</Button>
            </Stack>
            {erro && <Alert severity="error">{erro}</Alert>}
            {carregando && <CircularProgress size={24} aria-label="Consultando arquivos" />}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label="Arquivos de retorno"><TableHead><TableRow>
                    {['Arquivo', 'Geração', 'Sequencial', 'Situação', 'Consulta'].map(t => <TableCell key={t}>{t}</TableCell>)}
                </TableRow></TableHead><TableBody>
                    {lista.map(r => <TableRow key={r.uuid} selected={r.uuid === selecionado}>
                        <TableCell sx={{ overflowWrap: 'anywhere' }}>{r.nomeOriginal}</TableCell><TableCell>{dataRetorno(r.dataGeracao)}</TableCell>
                        <TableCell>{r.sequencialBanco}</TableCell><TableCell>{rotuloRetorno(r.situacao)}</TableCell>
                        <TableCell><Button type="button" disabled={enviando} onClick={() => abrir(r.uuid)}>Ver ocorrências</Button></TableCell>
                    </TableRow>)}
                    {!carregando && !erro && !lista.length && <TableRow><TableCell colSpan={5}>Nenhum retorno encontrado nesta página.</TableCell></TableRow>}
                </TableBody></Table>
            </TableContainer>
            <Paginacao total={total} valor={pagina} alterar={setPagina} disabled={carregando || enviando} />
            {selecionado && <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} /><Typography variant="h6">Ocorrências do arquivo</Typography>
                {lendo && <CircularProgress size={24} aria-label="Consultando ocorrências" />}
                {erroDetalhe && <Alert severity="error">{erroDetalhe}</Alert>}
                {detalhe && <>
                    <Typography sx={{ mb: 2, overflowWrap: 'anywhere' }}>{detalhe.arquivo.nomeOriginal} — {detalhe.meta.totalRowCount} ocorrência(s)</Typography>
                    {(detalhe.arquivo.avisosExibicao || detalhe.arquivo.avisos || []).map((aviso, i) => <Alert key={i} severity="info" sx={{ mb: 1 }}>{aviso}</Alert>)}
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        {detalhe.data.map(e => <Paper key={e.uuid} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                <Typography variant="subtitle1">{e.movimentoCodigo} — {e.descricaoMovimento}</Typography>
                                <Chip size="small" label={`Vínculo: ${rotuloRetorno(e.situacaoVinculo)}`} color={e.situacaoVinculo === 'pendente' ? 'warning' : 'default'} />
                                <Chip size="small" label={`Duplicidade: ${rotuloRetorno(e.situacaoDuplicidade)}`} color={e.situacaoDuplicidade === 'suspeita' ? 'warning' : 'default'} />
                            </Stack>
                            <Typography variant="body2" sx={{ my: 1, overflowWrap: 'anywhere' }}>Nosso número: {e.nossoNumero || 'Não informado'} · Documento: {e.documento || 'Não informado'} · Carteira: {e.carteira || 'Não informada'}</Typography>
                            <Typography variant="body2">Vencimento: {dataRetorno(e.vencimento)} · Ocorrência: {dataRetorno(e.dataOcorrencia)} · Crédito: {dataRetorno(e.dataCredito)}</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, my: 2 }}>
                                {dinheiro.map(([k, label]) => <Box key={k}><Typography variant="body2">{label}</Typography><Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{moedaRetorno(e[k])}</Typography></Box>)}
                            </Box>
                            {(e.motivos || []).map((m, i) => <Typography key={i} variant="body2">Motivo {m.codigo}: {m.descricao || 'Descrição não informada'}</Typography>)}
                            {(e.pendencias || []).map((p, i) => <Alert key={i} severity="warning" sx={{ mt: 1 }}>{p}</Alert>)}
                            <Typography variant="caption" component="p" sx={{ mt: 1 }}>Lote {e.lote} · Linhas {e.linhaInicial} a {e.linhaFinal}</Typography>
                        </Paper>)}
                    </Stack>
                    <Paginacao total={detalhe.meta.totalRowCount} valor={paginaEventos} alterar={setPaginaEventos} disabled={lendo || enviando} />
                </>}
            </Box>}
        </DialogContent>
        <DialogActions><Button type="button" disabled={enviando} onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>;
}
